import React, { useState, useMemo } from 'react';
import { fieldProps } from './missingFields';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { countries, filterCountries, getCountryByCode, isIndia } from '../../../utils/countries';
import { getStateOptions } from '../../../utils/states';
import { capitalizeWords } from '../../../utils/formatters';
import { StepHeader, StepNote } from './StepHeader';
import LockedField from './LockedField';
import { useLocked } from '../hooks/useLocked';
import FieldFlag from './FieldFlag';

/** Registered-address field → the supply field "Same as registered address" copies it into. */
const SUPPLY_MIRROR = {
  registeredAddress: 'supplyAddress',
  registeredCity: 'supplyCity',
  registeredState: 'supplyState',
  registeredDistrict: 'supplyDistrict',
  registeredCountry: 'supplyCountry',
  registeredPincode: 'supplyPincode',
};

const AddressDetailsStep = ({ formData, updateFormData, errors, flaggedFields, locked, lockSources }) => {
  const lock = useLocked(locked, lockSources, 2);
  const [registeredCountrySearch, setRegisteredCountrySearch] = useState('');
  const [supplyCountrySearch, setSupplyCountrySearch] = useState('');
  const [isRegisteredCountryOpen, setIsRegisteredCountryOpen] = useState(false);
  const [isSupplyCountryOpen, setIsSupplyCountryOpen] = useState(false);

  // Filter countries based on search terms
  const filteredRegisteredCountries = useMemo(() => {
    return filterCountries(registeredCountrySearch);
  }, [registeredCountrySearch]);

  const filteredSupplyCountries = useMemo(() => {
    return filterCountries(supplyCountrySearch);
  }, [supplyCountrySearch]);

  const handleInputChange = (field, value) => {
    // Capitalize first letter of every word for specific fields
    const fieldsToCapitalize = ['registeredAddress', 'registeredCity', 'registeredState', 'supplyAddress', 'supplyCity', 'supplyState'];
    if (fieldsToCapitalize.includes(field)) {
      value = capitalizeWords(value);
    }
    // "Same as registered address" disables the supply inputs, so keep them in step with what is typed
    // above; otherwise a supply field that was empty when the box was ticked could never be filled.
    const mirror = formData.sameAsRegistered && !lock.isLocked(field) && SUPPLY_MIRROR[field];
    updateFormData(mirror ? { [field]: value, [mirror]: value } : { [field]: value });
  };

  const handleRegisteredCountrySelect = (countryCode) => {
    handleInputChange('registeredCountry', countryCode);
    setIsRegisteredCountryOpen(false);
    setRegisteredCountrySearch('');
  };

  const handleSupplyCountrySelect = (countryCode) => {
    handleInputChange('supplyCountry', countryCode);
    setIsSupplyCountryOpen(false);
    setSupplyCountrySearch('');
  };

  const handleSameAsRegistered = (checked) => {
    if (checked) {
      updateFormData({
        sameAsRegistered: true,
        supplyAddress: formData.registeredAddress,
        supplyCity: formData.registeredCity,
        supplyState: formData.registeredState,
        supplyDistrict: formData.registeredDistrict,
        supplyCountry: formData.registeredCountry,
        supplyPincode: formData.registeredPincode
      });
    } else {
      updateFormData({
        sameAsRegistered: false,
        supplyAddress: '',
        supplyCity: '',
        supplyState: '',
        supplyDistrict: '',
        supplyCountry: '',
        supplyPincode: ''
      });
    }
  };

  const isRegisteredIndian = isIndia(formData.registeredCountry);
  const isSupplyIndian = isIndia(formData.supplyCountry);
  const registeredCountry = getCountryByCode(formData.registeredCountry);
  const supplyCountry = getCountryByCode(formData.supplyCountry);

  const registeredStateOptions = getStateOptions(formData.registeredCountry);
  const supplyStateOptions = getStateOptions(formData.supplyCountry);

  return (
    <div className="space-y-8">
      <StepHeader
        title="Address Details"
        description="Your registered office and the address goods are supplied from."
      >
        {lock.any ? (
          <StepNote tone="success" icon="ShieldCheck">
            Registered office pre-filled from GST records — city and district may need a tweak. Supply address defaults to the same.
          </StepNote>
        ) : (
          formData.countryOrigin && (
            <StepNote tone="success">
              Country pre-filled from Company Information — change it here if your addresses are elsewhere.
            </StepNote>
          )
        )}
      </StepHeader>

      {/* Registered Address */}
      <div className="bg-muted/30 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-foreground mb-4">Registered Office Address</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            {lock.isLocked('registeredAddress') ? (
              <LockedField label="Address Line 1" value={formData.registeredAddress} source={lock.sourceOf('registeredAddress')} required />
            ) : (
              <Input
                label="Address Line 1"
                type="text"
                placeholder="Enter address line 1"
                value={formData.registeredAddress}
                {...fieldProps('registeredAddress')}
                onChange={(e) => handleInputChange('registeredAddress', e.target.value)}
                error={errors.registeredAddress}
                required
              />
            )}
          </div>

          <Input
            label="City"
            type="text"
            placeholder="Enter city"
            value={formData.registeredCity}
            {...fieldProps('registeredCity')}
            onChange={(e) => handleInputChange('registeredCity', e.target.value)}
            error={errors.registeredCity}
            required
          />

          {/* Registered Country Dropdown */}
          {lock.isLocked('registeredCountry') ? (
            <LockedField label="Country" displayValue={registeredCountry ? registeredCountry.label : formData.registeredCountry} source={lock.sourceOf('registeredCountry')} required />
          ) : (
          <div className="relative">
            <label className="block text-sm font-medium text-foreground mb-2">
              Country <span className="text-destructive">*</span>
              {formData.registeredCountry && (
                <span className="ml-2 text-xs text-success bg-success/10 px-2 py-1 rounded">
                  ✓ Auto-filled from Company Information
                </span>
              )}
            </label>
            <div className="relative">
              <button
                type="button"
                {...fieldProps('registeredCountry')}
                onClick={() => setIsRegisteredCountryOpen(!isRegisteredCountryOpen)}
                className={`w-full px-3 py-2 text-left border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
                  errors.registeredCountry ? 'border-destructive' : 'border-border'
                } ${formData.registeredCountry ? 'text-foreground' : 'text-muted-foreground'} ${
                  formData.registeredCountry && formData.registeredCountry === formData.countryOrigin 
                    ? 'bg-success/5 border-success/40' 
                    : ''
                }`}
              >
                {registeredCountry ? registeredCountry.label : 'Select a country'}
              </button>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {isRegisteredCountryOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-hidden">
                <div className="p-2 border-b border-border">
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={registeredCountrySearch}
                    onChange={(e) => setRegisteredCountrySearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {filteredRegisteredCountries.length > 0 ? (
                    filteredRegisteredCountries.map((country) => (
                      <button
                        key={country.value}
                        type="button"
                        onClick={() => handleRegisteredCountrySelect(country.value)}
                        className={`w-full px-4 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none ${
                          formData.registeredCountry === country.value ? 'bg-primary text-white hover:bg-primary/90' : ''
                        }`}
                      >
                        {country.label}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-muted-foreground">No countries found</div>
                  )}
                </div>
              </div>
            )}

            {errors.registeredCountry && (
              <p className="mt-1 text-sm text-destructive">{errors.registeredCountry}</p>
            )}
          </div>
          )}

          <div>
            {lock.isLocked('registeredState') ? (
              <LockedField
                label="State"
                displayValue={(registeredStateOptions.find((o) => o.value === formData.registeredState) || {}).label || formData.registeredState}
                source={lock.sourceOf('registeredState')}
                required
              />
            ) : registeredStateOptions.length > 0 ? (
              <Select
                label={isRegisteredIndian ? "State" : "State/Province"}
                options={registeredStateOptions}
                value={formData.registeredState}
                {...fieldProps('registeredState')}
                onChange={(value) => handleInputChange('registeredState', value)}
                error={errors.registeredState}
                searchable
                required
              />
            ) : (
              <Input
                label="State/Province"
                type="text"
                placeholder="Enter state or province"
                value={formData.registeredState}
                {...fieldProps('registeredState')}
                onChange={(e) => handleInputChange('registeredState', e.target.value)}
                error={errors.registeredState}
                required
              />
            )}
            <FieldFlag flaggedFields={flaggedFields} name="registeredState" />
          </div>

          <Input
            label="District"
            type="text"
            placeholder="Enter district (optional)"
            value={formData.registeredDistrict}
            onChange={(e) => handleInputChange('registeredDistrict', e.target.value)}
            error={errors.registeredDistrict}
          />

          {lock.isLocked('registeredPincode') ? (
            <LockedField label="PIN Code" value={formData.registeredPincode} source={lock.sourceOf('registeredPincode')} required />
          ) : (
            <Input
              label={isRegisteredIndian ? "PIN Code" : "Postal Code"}
              type="text"
              placeholder={isRegisteredIndian ? "Enter PIN code" : "Enter postal code"}
              value={formData.registeredPincode}
              {...fieldProps('registeredPincode')}
              onChange={(e) => handleInputChange('registeredPincode', e.target.value)}
              error={errors.registeredPincode}
              required
            />
          )}
        </div>
      </div>

      {/* Supply Address */}
      <div className="bg-muted/30 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Supply Address</h3>
          <Checkbox
            label="Same as registered address"
            checked={formData.sameAsRegistered}
            onChange={(e) => handleSameAsRegistered(e.target.checked)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Input
              label="Address Line 1"
              type="text"
              placeholder="Enter address line 1"
              value={formData.supplyAddress}
              {...fieldProps('supplyAddress')}
              onChange={(e) => handleInputChange('supplyAddress', e.target.value)}
              error={errors.supplyAddress}
              disabled={formData.sameAsRegistered}
              required
            />
          </div>

          <Input
            label="City"
            type="text"
            placeholder="Enter city"
            value={formData.supplyCity}
            {...fieldProps('supplyCity')}
            onChange={(e) => handleInputChange('supplyCity', e.target.value)}
            error={errors.supplyCity}
            disabled={formData.sameAsRegistered}
            required
          />

          {/* Supply Country Dropdown */}
          <div className="relative">
            <label className="block text-sm font-medium text-foreground mb-2">
              Country <span className="text-destructive">*</span>
              {formData.supplyCountry && (
                <span className="ml-2 text-xs text-success bg-success/10 px-2 py-1 rounded">
                  ✓ Auto-filled from Company Information
                </span>
              )}
            </label>
            <div className="relative">
              <button
                type="button"
                {...fieldProps('supplyCountry')}
                onClick={() => !formData.sameAsRegistered && setIsSupplyCountryOpen(!isSupplyCountryOpen)}
                disabled={formData.sameAsRegistered}
                className={`w-full px-3 py-2 text-left border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
                  errors.supplyCountry ? 'border-destructive' : 'border-border'
                } ${formData.supplyCountry ? 'text-foreground' : 'text-muted-foreground'} ${
                  formData.sameAsRegistered ? 'bg-muted cursor-not-allowed' : 'cursor-pointer'
                } ${
                  formData.supplyCountry && formData.supplyCountry === formData.countryOrigin && !formData.sameAsRegistered
                    ? 'bg-success/5 border-success/40' 
                    : ''
                }`}
              >
                {supplyCountry ? supplyCountry.label : 'Select a country'}
              </button>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {isSupplyCountryOpen && !formData.sameAsRegistered && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-hidden">
                <div className="p-2 border-b border-border">
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={supplyCountrySearch}
                    onChange={(e) => setSupplyCountrySearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {filteredSupplyCountries.length > 0 ? (
                    filteredSupplyCountries.map((country) => (
                      <button
                        key={country.value}
                        type="button"
                        onClick={() => handleSupplyCountrySelect(country.value)}
                        className={`w-full px-4 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none ${
                          formData.supplyCountry === country.value ? 'bg-primary text-white hover:bg-primary/90' : ''
                        }`}
                      >
                        {country.label}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-muted-foreground">No countries found</div>
                  )}
                </div>
              </div>
            )}

            {errors.supplyCountry && (
              <p className="mt-1 text-sm text-destructive">{errors.supplyCountry}</p>
            )}
          </div>

          {supplyStateOptions.length > 0 ? (
            <Select
              label={isSupplyIndian ? "State" : "State/Province"}
              options={supplyStateOptions}
              value={formData.supplyState}
              {...fieldProps('supplyState')}
              onChange={(value) => handleInputChange('supplyState', value)}
              error={errors.supplyState}
              disabled={formData.sameAsRegistered}
              searchable
              required
            />
          ) : (
            <Input
              label="State/Province"
              type="text"
              placeholder="Enter state or province"
              value={formData.supplyState}
              {...fieldProps('supplyState')}
              onChange={(e) => handleInputChange('supplyState', e.target.value)}
              error={errors.supplyState}
              disabled={formData.sameAsRegistered}
              required
            />
          )}

          <Input
            label="District"
            type="text"
            placeholder="Enter district (optional)"
            value={formData.supplyDistrict}
            onChange={(e) => handleInputChange('supplyDistrict', e.target.value)}
            error={errors.supplyDistrict}
            disabled={formData.sameAsRegistered}
          />

          <Input
            label={isSupplyIndian ? "PIN Code" : "Postal Code"}
            type="text"
            placeholder={isSupplyIndian ? "Enter PIN code" : "Enter postal code"}
            value={formData.supplyPincode}
            {...fieldProps('supplyPincode')}
            onChange={(e) => handleInputChange('supplyPincode', e.target.value)}
            error={errors.supplyPincode}
            disabled={formData.sameAsRegistered}
            required
          />
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(isRegisteredCountryOpen || isSupplyCountryOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsRegisteredCountryOpen(false);
            setIsSupplyCountryOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default AddressDetailsStep;